import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { CandidateStatBar } from './CandidateStatBar'
import { RECRUITMENT_SOURCE_INFO } from '@/constants/recruitmentSources'
import { IdeologyMini } from '@/components/ideology/IdeologyDisplay'
import { IdeologyAlignmentBadge } from '@/components/ideology/IdeologyAlignmentBadge'
import type { GeneratedCandidate } from '@/lib/candidateGenerator'
import type { IdeologyVector } from '@/types'
import { cn } from '@/lib/utils'

interface CandidateOptionCardProps {
  candidate: GeneratedCandidate
  partyIdeology: IdeologyVector
  selected: boolean
  onSelect: () => void
  className?: string
}

export function CandidateOptionCard({
  candidate,
  partyIdeology,
  selected,
  onSelect,
  className,
}: CandidateOptionCardProps) {
  const sourceInfo = RECRUITMENT_SOURCE_INFO[candidate.recruitment_source]

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all',
        selected && 'ring-2 ring-primary border-primary',
        !selected && 'hover:border-muted-foreground/50',
        className
      )}
      onClick={onSelect}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <CardTitle className="flex items-center gap-2 text-base">
              {selected && <span className="text-primary">●</span>}
              {!selected && <span className="text-muted-foreground">○</span>}
              <span className="truncate">{candidate.name}</span>
            </CardTitle>
            <div className="text-muted-foreground mt-0.5 text-xs">
              Age: {candidate.age}
            </div>
          </div>
          {selected && (
            <Badge variant="default" className="shrink-0 text-xs">
              Selected
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <CandidateStatBar label="Loyalty" value={candidate.loyalty} colorMode="positive" />
          <CandidateStatBar label="Charisma" value={candidate.charisma} colorMode="positive" />
          <CandidateStatBar label="Admin" value={candidate.admin_skill} colorMode="positive" />
          <CandidateStatBar label="Debate" value={candidate.debate_skill} colorMode="positive" />
        </div>

        <div className="border-t pt-3">
          <div className="text-muted-foreground mb-1.5 text-xs font-medium">Risk Factors</div>
          <div className="space-y-1.5">
            <CandidateStatBar
              label="Scandal"
              value={candidate.scandal_prone}
              colorMode="negative"
            />
            <CandidateStatBar label="Ambition" value={candidate.ambition} colorMode="neutral" />
          </div>
        </div>

        <div className="border-t pt-3">
          <div className="text-muted-foreground mb-1.5 text-xs font-medium">Ideology</div>
          <div className="flex items-center justify-between gap-2">
            <IdeologyMini ideology={candidate.ideology_vector} />
            <IdeologyAlignmentBadge
              candidateIdeology={candidate.ideology_vector}
              partyIdeology={partyIdeology}
            />
          </div>
        </div>

        <div className="border-t pt-2">
          <Badge variant="outline" className="text-xs">
            {sourceInfo.name}
          </Badge>
        </div>
      </CardContent>
    </Card>
  )
}
